import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Toolbar from '../../components/Toolbar';
import { PATH, PolicyStatus } from '../../constants';
import autoPaymentStore, { AutoPaymentStore } from '../../stores/AutoPaymentStore';
import { formatMoney, removeLeadingZeros, formatDate } from 'src/utils';

import './style.scoped.css';
import ColorPink from 'src/assets/Color_Pink.svg';
import ColorBlue from 'src/assets/Color_Blue.svg';
import Button from 'src/components/Button';
import DetailInfoElement from 'src/components/DetailInfoItem';
import { AutoDebitItem } from '../../models/AutoDebit';
import Policy from '../../models/Policy';
import LoadingService from '../../utils/LoadingService';

const Card = observer((props: {
  detail:  Policy,
  handleClick: Function
}) => {
  const {
    detail = {},
    handleClick = (detail: any) => {
    },
  } = props;
  const { t } = useTranslation();
  const [showDetail, setShowDetail] = useState(false);

  const _handleClickButton = () => {
    handleClick(detail);
  };

  return (
    <div className="wapper_card mb-4 ">
      <div className="item">
        <div
          className={`item-view ${
            detail.policyStatusId == PolicyStatus.Active ? 'cl-pink' : 'cl-blue'
          } p-10ct`}
        >
          <div className="layout-top">
            <div className="lf"></div>
            <div className="rf">
              <div className="title pb-5px">{detail.productName}</div>
              <div className="number-phone">
                {removeLeadingZeros(detail.policyNum)}{' '}
              </div>
              {detail.policyStatusId == PolicyStatus.Active && (
                <div className="active-card cl-green d-inline-block">
                  {t('active')}
                </div>
              )}
              {detail.policyStatusId == PolicyStatus.Lapsed && (
                <div className="disconnect-card cl-gray d-inline-block">
                  {t('lapsed')}
                </div>
              )}
            </div>
          </div>
          <div className="layout-buttom">
            <div className="lf">
              <div className="title-buy">{t('insurance_buyer')}</div>
              <div className="name-buy">{detail.policyHolderName}</div>
            </div>
            <div className="rf">
              <div className="logo-ct"></div>
            </div>
          </div>
          <div className="btn-detail">
            <button
              className="icon-btn"
              onClick={() => setShowDetail(!showDetail)}
            >
              <i
                className={'arrow-ct' + (!showDetail ? ' down-ct' : ' up-ct')}
              ></i>
            </button>
          </div>
          <div className="bg-cl-ping">
            <img
              src={
                detail.policyStatusId == PolicyStatus.Active
                  ? ColorPink
                  : ColorBlue
              }
              alt=""
              className="w-100"
            />
          </div>
        </div>

        {
          showDetail &&
          <div className="show-detail">
            <div className={'px-4 show-detail-top border border-line border-radius-bottom'}>
              <DetailInfoElement title={t('policy_number')} content={detail?.policyNum} />
              <DetailInfoElement title={t('main_product_name')} content={detail?.productName} />
              <DetailInfoElement title={t('policy_start_date_1')} content={detail?.polStatrtDat} />
              <DetailInfoElement title={t('pay_frequency')} content={detail?.payFrequency} />
              <DetailInfoElement title={t('fee_frequency')} content={
                detail?.periodicPremium && detail?.currency ?
                  formatMoney(detail.periodicPremium)
                  + ' '
                  + detail?.currency
                  : null
              } />

              <div className={'show-detail-bottom btn-submit '}>
                <Button
                  text={t('register')}
                  onClick={_handleClickButton}
                  extraClassName={'w-full'}
                />
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
});


interface RegisterAutoPaymentObserverProps {
  store?: AutoPaymentStore;
}

const RegisterAutoPaymentObserver = observer(({ store }: RegisterAutoPaymentObserverProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [policies, setPolicies] = useState<Policy[]>([]);

  useEffect(() => {
    LoadingService.instance.current.open()
    Promise.all([store.getAutoDebits(false), store.getPolicies(false)]).then(
      ([autoDebits, policies]) => {
        setPolicies(mapPolices(policies, autoDebits));
      },
    ).finally(() => {
      LoadingService.instance.current.close()
    });
  }, []);

  const mapPolices = (policies: Policy[], autoDebits?: AutoDebitItem[]) => {
    if (!policies || !autoDebits) {
      return policies ?? [];
    }
    const autoDebitMap: Map<string, AutoDebitItem> = new Map(
      autoDebits.map((autoDebitItem) => {
        return [
          autoDebitItem.productId,
          autoDebitItem
        ]
      })
    )

    return policies.filter(
      (policy) => (
        !autoDebitMap.has(policy.policyNum) &&
        policy.policyStatusId == PolicyStatus.Active
      )
    )
  };

  const _handleClose = useCallback(() => {
    history.push('/');
  }, []);

  const _handleBack = useCallback(() => {
    history.push(PATH.PAYMENT_ONLINE);
  }, []);

  const navigateToConfirm = useCallback((detail: any) => {
    history.push(PATH.CONFIRM_REGISTER_AUTO_PAYMENT, { detail });
  }, []);

  return (
    <div className="screen-container bg-background ct-view fix-scroll">
      <Toolbar
        title={t('register_auto_payment')}
        showClose={true}
        onClose={_handleClose}
        onBack={_handleBack}
      />
      <div className={'max-w-md mx-auto my-policy' + (policies.length > 0 ? ' screenScrollContainer' : '')}>
        <div className="bg-surface px-4 pt-4 pb-4 mt-4 border-b border-line">
          <div className="text-base font-bold text-onSurface">
            {`${t('list_policy')} (${policies.length})`}
          </div>
        </div>
        <div className="bg-surface py-4 px-5 mb-8">
          {policies.length > 0 ? policies.map((policy, index) =>
            <div className="pb-4" key={index}>
              <Card detail={policy} handleClick={() => navigateToConfirm(policy)} />
            </div>,
          ) : <div className="px-4 pt-4 h-36">{t('no_policy')}</div>}
        </div>
      </div>
    </div>
  );
});

const RegisterAutoPayment = () => <RegisterAutoPaymentObserver store={autoPaymentStore} />;
export default RegisterAutoPayment;
